import type { ToRefs } from 'vue'
import type { Meeting } from '~~/entities/meeting'

export interface CreateMeetingParams {
  companyId: number
  title?: string | null
  is_caption_enabled?: boolean
  is_audio_video_enabled?: boolean
  deck_id?: number | null
  description?: string | null
  scheduled_start_at?: string | null
  scheduled_end_at?: string | null
}

export interface EndMeetingParams {
  shareId: string
}

export interface CreateRecordingParams {
  companyId: number
  title: string
  deckId: number
}

export interface UpdateMeetingParams {
  share_id?: string
  title?: string | null
  description?: string | null
  scheduled_start_at?: string | null
  scheduled_end_at?: string | null
}

export interface UseMeetingsState {
  loading: boolean
  error: Error | null
  meetings: Meeting[]
  pastMeetings: Meeting[]
  currentMeeting: Meeting | null
}

export interface UseMeetings extends ToRefs<UseMeetingsState> {
  createMeeting: (params: CreateMeetingParams) => Promise<Meeting>
  startMeeting: (params: { shareId: string }) => Promise<Meeting>
  endMeeting: (params: EndMeetingParams) => Promise<Meeting>
  getMeeting: (params: { shareId: string }) => Promise<Meeting>
  setCurrentMeeting: (meeting: Meeting | null) => void
  isCurrentMeetingActive: ComputedRef<boolean>
}

export function useMeetings(): UseMeetings {
  const state = useState<UseMeetingsState>('meetings', () => ({
    loading: false,
    error: null,
    meetings: [],
    pastMeetings: [],
    currentMeeting: null,
  }))

  const isCurrentMeetingActive = computed(() => {
    return state.value.currentMeeting?.ended_at === null
  })

  async function getMeeting(params: { shareId: string }): Promise<Meeting> {
    return await $fetch<Meeting>(`v2/conferences/${params.shareId}`, {
      ...useFetchSharedOptions(),
      params: { include: ['host'] },
    })
  }

  async function createMeeting({ companyId, ...params }: CreateMeetingParams): Promise<Meeting> {
    return await $fetch<Meeting>(`v2/companies/${companyId}/conferences`, {
      method: 'POST',
      body: {
        is_caption_enabled: false,
        ...params,
      },
      ...useFetchSharedOptions(),
    })
  }

  async function startMeeting(params: { shareId: string }): Promise<Meeting> {
    return await $fetch<Meeting>(`v2/conferences/${params.shareId}/start`, {
      ...useFetchSharedOptions(),
      params: { include: ['host'] },
      method: 'POST',
    })
  }

  async function endMeeting(params: EndMeetingParams): Promise<Meeting> {
    return await $fetch<Meeting>(`v2/conferences/${params.shareId}/end`, { ...useFetchSharedOptions(), method: 'POST' })
  }

  function setCurrentMeeting(meeting: Meeting | null) {
    state.value.currentMeeting = meeting
  }

  return {
    ...toRefs(state.value),
    createMeeting,
    startMeeting,
    endMeeting,
    getMeeting,
    setCurrentMeeting,
    isCurrentMeetingActive,
  }
}
